import {useEffect, useMemo, useRef, useState} from 'react';
import {useBeforeUnload, useBlocker, useNavigate, useSearchParams} from 'react-router-dom';
import styled from 'styled-components';
import {getGqlErrorMessage, isAccessRestrictionError} from '@/api/request';
import {useJoin, useResetPassword} from '@/api/session';
import {Button, ButtonGroup} from '@/components/Button';
import toast from '@/components/HotToast';
import {Modal} from '@/components/Modal';
import {NavBar} from '@/components/NavBar';
import {Typography} from '@/components/Typography';
import {EmailStep, ExtraInfoStep, UserPurposeStep} from '@/components/UserSession';
import {AgreementStep} from '@/components/UserSession/Agreement';
import {MobileNavigator} from '@/components/UserSession/MobileNavigator';
import {PhoneVerifyStep} from '@/components/UserSession/PhoneVerify';
import {SetPasswordStep} from '@/components/UserSession/SetPassword';
import {SignInPasswordStep} from '@/components/UserSession/SignInPassword';
import {Body, FormWrapper, Title} from '@/components/UserSession/StyledComponents';
import {SignInActionType, SignInSteps, UserProfileType} from '@/enums';
import {useBreakpoints} from '@/hooks';
import {useGA4WithParams} from '@/hooks/tracking';
import {IS_DEV_SERVER, paths} from '@/router';
import {ColumnFlexBox, HorizontalFullContainer, PaddingBox} from '@/styled-components';
import {MEDIA_QUERIES, palette} from '@/styles';
import {isMyfcEmailAccount, trackAirbridge} from '@/utils';
import DoneImage from './done.gif';

const STEPS = {
  [SignInActionType.signin]: [SignInSteps.email, SignInSteps.signinPassword],
  [SignInActionType.resetPassword]: [
    SignInSteps.resetPasswordverifyPhone,
    SignInSteps.resetPassword,
  ],
  [SignInActionType.signup]: [
    SignInSteps.signupPassword,
    SignInSteps.verifyPhone,
    SignInSteps.extraInfo,
    SignInSteps.userPurpose,
    SignInSteps.agreement,
  ],
};

const STEP_TITLES: {
  [key: string]: {
    title: string;
    subTitle?: string;
  };
} = {
  [SignInSteps.email]: {
    title: '이메일로 시작하기',
  },
  [SignInSteps.signinPassword]: {
    title: '로그인',
  },
  [SignInSteps.signupPassword]: {
    title: '비밀번호 설정',
  },
  [SignInSteps.verifyPhone]: {title: '휴대전화 번호 인증'},
  [SignInSteps.extraInfo]: {
    title: '추가 정보',
    subTitle: '필요한 정보를 제공할 수 있도록 회원님에 대해 알려주세요 ☺️',
  },
  [SignInSteps.userPurpose]: {
    title: '사용 목적',
    subTitle: '지도를 잘 활용하실 수 있도록 돕기 위해 목적을 알려주세요.',
  },
  [SignInSteps.agreement]: {title: '약관 동의'},
};

const SignIn = () => {
  const [values, setValues] = useState<{[key: string]: any}>({});
  const [step, setStep] = useState<SignInSteps>(SignInSteps.email);

  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  const tracking = useGA4WithParams();

  const {
    mutate: join,
    isPending: isJoinPending,
    isSuccess: isJoinSuccess,
  } = useJoin();
  const {mutate: resetPassword, isPending: isPendingResetPassword} =
    useResetPassword();
  const {isMobile} = useBreakpoints();

  const actionType: SignInActionType = useMemo(() => {
    switch (step) {
      case SignInSteps.email:
      case SignInSteps.signinPassword:
        return SignInActionType.signin;
      case SignInSteps.resetPasswordverifyPhone:
      case SignInSteps.resetPassword:
      case SignInSteps.resetPasswordCompleted:
        return SignInActionType.resetPassword;
      default:
        return SignInActionType.signup;
    }
  }, [step]);

  const onChangeValue = (value: {[key: string]: any}) => {
    setValues((prev) => ({...prev, ...value}));
  };

  const onChangeProfile = (value: {[key: string]: any}) => {
    onChangeValue({profile: {...values.profile, ...value}});
  };

  const onChangeStep = (nextStep: SignInSteps, replace?: boolean) => {
    setSearchParams(
      {step: nextStep},
      {replace: replace || !step || nextStep === step}
    );
  };

  const onSetPasswordComplete = () => {
    if (step === SignInSteps.resetPassword) {
      resetPassword(
        {
          cellPhone: values.cellPhone,
          password: values.password,
          smsVerificationId: values.smsVerificationId,
        },
        {
          onSuccess: (res) => {
            if (res.result) {
              onChangeStep(SignInSteps.resetPasswordCompleted);
            } else {
              toast.error(
                '비밀번호 변경 실패했습니다. 오류가 계속된다면 고객센터로 연락 주세요.'
              );
            }
          },
          onError: (error) => {
            toast.error(
              getGqlErrorMessage(
                error,
                '비밀번호 변경 실패했습니다. 오류가 계속된다면 고객센터로 연락 주세요.'
              )
            );
          },
        }
      );
    } else {
      onChangeStep(SignInSteps.verifyPhone);
    }
  };

  const onJoin = (isMarketingAgreed: boolean) => {
    join({...values, isMarketingAgreed} as any, {
      onSuccess: () => {
        tracking('signup');
        if (IS_DEV_SERVER || !isMyfcEmailAccount(values.email)) {
          trackAirbridge('airbridge.user.signup', {
            action: values.profile?.[UserProfileType.gender],
            label: values?.profile?.[UserProfileType.ageGroup],
            semanticAttributes: {
              category: values?.profile?.[UserProfileType.category],
              categoryOther: values?.profile?.[UserProfileType.otherCategory],
            },
            customAttributes: {
              purpose: values?.profile?.[UserProfileType.purpose],
              purposeOther: values?.profile?.[UserProfileType.other],
            },
          });
        }
        navigate(paths.welcome, {state: {fromJoin: true}});
      },
      onError: (error) => {
        // 제한 중인 이메일·연락처(ACCOUNT_RESTRICTED)는 공통 에러 핸들러가 계정용 안내 모달을 띄운다.
        if (isAccessRestrictionError(error)) {
          return;
        }

        toast.error(
          getGqlErrorMessage(
            error,
            '회원가입 실패했습니다. 오류가 계속된다면 고객센터로 연락 주세요.'
          )
        );
      },
    });
  };

  const prevStep = () => {
    navigate(-1);
  };

  const clear = () => {
    setValues({});
    onChangeStep(SignInSteps.email);
  };

  let blocker = useBlocker(
    ({currentLocation, nextLocation}) =>
      nextLocation.pathname !== paths.welcome &&
      (currentLocation.pathname !== nextLocation.pathname ||
        !nextLocation.search) &&
      actionType === SignInActionType.signup &&
      !isJoinSuccess
  );

  useBeforeUnload((e: BeforeUnloadEvent) => {
    if (actionType === SignInActionType.signup && !isJoinSuccess) {
      e.preventDefault();
    }
  });

  useEffect(() => {
    const paramsStep = searchParams.get('step') as SignInSteps;

    if (paramsStep?.length && step === paramsStep) {
      return;
    }

    if (paramsStep) {
      if (
        paramsStep !== SignInSteps.email &&
        (!Object.keys(values).length ||
          !Object.values(SignInSteps).includes(paramsStep))
      ) {
        onChangeStep(SignInSteps.email);
      } else {
        setStep(paramsStep);
      }
    } else {
      clear();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  if (step === SignInSteps.resetPasswordCompleted) {
    return <ResetPasswordCompleted />;
  }

  const steps = STEPS[actionType];
  const progress = steps
    ? ((steps.findIndex((i) => i === step) + 1) / STEPS[actionType].length) *
      100
    : 0;

  return (
    <HorizontalFullContainer>
      {isMobile ? (
        <MobileNavigator
          onPrev={step === SignInSteps.email ? undefined : prevStep}
          onClose={() => navigate(paths.home)}
        />
      ) : (
        <NavBar logoOnly />
      )}
      {blocker.state === 'blocked' && (
        <Modal
          title="회원가입 취소 안내"
          width={328}
          onClose={() => blocker?.reset?.()}
        >
          <PaddingBox spacing={24}>
            <Typography variant="medium14" color={palette.gray600}>
              페이지를 벗어날 경우 회원가입이 완료되지 않습니다. 이 페이지에서
              나가시겠습니까?
            </Typography>
            <PaddingBox top={24}>
              <ButtonGroup flex={1} spacing={2}>
                <Button
                  variant="outline"
                  color="black"
                  size="mediumLarge"
                  onClick={() => blocker?.reset?.()}
                >
                  취소
                </Button>
                <Button
                  variant="fill"
                  color="secondary"
                  size="mediumLarge"
                  onClick={() => blocker?.proceed?.()}
                >
                  나갈게요
                </Button>
              </ButtonGroup>
            </PaddingBox>
          </PaddingBox>
        </Modal>
      )}
      <Body>
        <FormWrapper>
          <StepProgress>
            {step !== SignInSteps.email && progress < 100 && (
              <StepProgressBar style={{width: `${progress}%`}} />
            )}
          </StepProgress>
          <FormContent>
            <Title>
              <ColumnFlexBox gap={8}>
                {actionType === SignInActionType.resetPassword
                  ? '비밀번호 재설정'
                  : STEP_TITLES[step]?.title}
                {!!STEP_TITLES[step]?.subTitle?.length && (
                  <Typography
                    variant="medium14"
                    color={palette.gray600}
                    fontWeight={400}
                    lineHeight="20px"
                    letterSpacing="-.3px"
                  >
                    {STEP_TITLES[step].subTitle}
                  </Typography>
                )}
              </ColumnFlexBox>
            </Title>
            {step === SignInSteps.email && (
              <EmailStep
                defaultValue={values.email}
                onChanged={onChangeValue}
                onComplete={onChangeStep}
              />
            )}
            {step === SignInSteps.signinPassword && (
              <SignInPasswordStep
                email={values.email}
                defaultValue={values.password}
                updatePassword={() =>
                  onChangeStep(SignInSteps.resetPasswordverifyPhone)
                }
              />
            )}
            {(step === SignInSteps.signupPassword ||
              step === SignInSteps.resetPassword) && (
              <SetPasswordStep
                defaultValue={values.password}
                loading={isPendingResetPassword}
                onChanged={onChangeValue}
                onComplete={onSetPasswordComplete}
              />
            )}
            {(step === SignInSteps.verifyPhone ||
              step === SignInSteps.resetPasswordverifyPhone) && (
              <PhoneVerifyStep
                type={
                  step === SignInSteps.resetPasswordverifyPhone
                    ? 'PWD_CHANGE'
                    : 'JOIN'
                }
                email={values.email}
                cellPhone={values.cellPhone}
                onChanged={onChangeValue}
                onVerified={onChangeValue}
                onComplete={() =>
                  onChangeStep(
                    step === SignInSteps.resetPasswordverifyPhone
                      ? SignInSteps.resetPassword
                      : SignInSteps.extraInfo
                  )
                }
                onClear={clear}
              />
            )}
            {step === SignInSteps.extraInfo && (
              <ExtraInfoStep
                defaultValues={values?.profile}
                onComplete={(value) => {
                  onChangeProfile(value);
                  onChangeStep(SignInSteps.userPurpose);
                }}
              />
            )}
            {step === SignInSteps.userPurpose && (
              <UserPurposeStep
                defaultValues={values?.profile}
                onComplete={(value) => {
                  onChangeProfile(value);
                  onChangeStep(SignInSteps.agreement);
                }}
              />
            )}
            {step === SignInSteps.agreement && (
              <AgreementStep loading={isJoinPending} onComplete={onJoin} />
            )}
          </FormContent>
        </FormWrapper>
      </Body>
    </HorizontalFullContainer>
  );
};

const ResetPasswordCompleted = () => {
  const navigate = useNavigate();
  const [remainTime, setRemainTime] = useState<number>(5);
  const timer = useRef<NodeJS.Timer>();

  const clearTimer = () => {
    if (timer.current) {
      clearTimeout(timer.current);
      timer.current = undefined;
    }
  };

  useEffect(() => {
    setRemainTime(5);

    timer.current = setInterval(() => {
      setRemainTime((prev) => prev - 1);
    }, 1000);

    return () => {
      clearTimer();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (remainTime === 0) {
      clearTimer();
      navigate({pathname: paths.signin}, {replace: true});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [remainTime]);

  return (
    <HorizontalFullContainer>
      <NavBar logoOnly />
      <Body>
        <FormWrapper>
          <FormContent>
            <Title style={{textAlign: 'center'}}>
              <ColumnFlexBox gap={16}>
                <span>
                  비밀번호가 성공적으로
                  <br />
                  재설정 되었습니다
                </span>
                <Typography
                  variant="medium12"
                  lineHeight="20px"
                  color={palette.gray600}
                  fontWeight="400"
                >
                  {remainTime}초 후 로그인 화면으로 이동합니다.
                </Typography>
              </ColumnFlexBox>
            </Title>
            <img src={DoneImage} width="100%" alt="재설정 완료" />
          </FormContent>
        </FormWrapper>
      </Body>
    </HorizontalFullContainer>
  );
};

export default SignIn;

const FormContent = styled.div`
  padding: 24px 26px 40px;
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  gap: 32px;
`;

const StepProgress = styled.div`
  width: 100%;
  height: 8px;
`;

const StepProgressBar = styled.div`
  background-color: ${palette.primary400};
  height: 100%;

  ${MEDIA_QUERIES.mobile} {
    display: none;
  }
`;
